import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  Table, TableHead, TableCell, TableRow, TableBody,
  CardContent,
  Divider,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import jsPDF from "jspdf";
import "jspdf-autotable";
import LoadingBar from "react-top-loading-bar";
import axios from "axios";

const PaymentPage = () => {
  const [paymentData, setPaymentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [schoolData, setSchoolData] = useState([]);
  const [logoDataUrl, setLogoDataUrl] = useState("");

  const API_URL = process.env.REACT_APP_BASE_URL;
  const token = sessionStorage.getItem("token");
  const studentId = sessionStorage.getItem("employeeId");
  const classId = sessionStorage.getItem("classId");
  const sectionId = sessionStorage.getItem("sectionId");

  // Initialize dates
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString()
      .split("T")[0];
  };

  const getCurrentDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  useEffect(() => {
    setFromDate(getFirstDayOfMonth());
    setToDate(getCurrentDate());
  }, []);

  // Fetch school details and logo
  const fetchSchoolDetails = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/FeeReport/GetSchoolName`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );
      setSchoolData(response.data);
      if (response.data?.[0]?.logoImg) {
        let cleanedBase64 = response.data[0].logoImg
          .replace(/^data:image\/[a-zA-Z]+;base64,/, "")
          .replace(/\s/g, "")
          .replace(/\n/g, "");
        const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
        cleanedBase64 += "=".repeat(paddingLength);
        setLogoDataUrl(`data:image/png;base64,${cleanedBase64}`);
      }
    } catch (error) {
      console.error("Error fetching school details:", error);
    }
  };

  // Fetch payment data
  const fetchPaymentDetails = async () => {
    setLoadingBarProgress(30);
    if (!studentId) {
      setError("Student ID not found in session storage");
      setLoading(false);
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/Student/FetchStudentPaymentDetails`,
        { studentID: studentId },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: token,
          },
        }
      );
      setPaymentData(response.data);
    } catch (err) {
      setError("Failed to fetch payment details");
    } finally {
      setLoading(false);
      setLoadingBarProgress(100);
    }
  };

  // Format date
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    const [day, month, year] = dateStr.split("/");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Generate report
  const generateReport = async () => {
    try {
      const formattedFromDate = formatDate(fromDate);
      const formattedToDate = formatDate(toDate);

      const requestBody = {
        isAllClass: false,
        isAllSection: false,
        isAllStudent: false,
        classId: parseInt(classId) || 0,
        sectionId: parseInt(sectionId) || 0,
        studentId: parseInt(studentId) || 0,
        fromDate: formattedFromDate,
        toDate: formattedToDate,
      };

      const response = await axios.post(
        `${API_URL}/FeeReport/ReportFeePayment`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );

      if (response.data.msg === "Record Not Found") {
        setSnackbarMessage("No records found!");
        setOpenSnackbar(true);
        return;
      }

      const reportWindow = window.open("", "_blank");
     reportWindow.document.write(`
    <html>
      <head>
        <title>Fee Payment Report</title>
        <style>
          body { font-family: 'Segoe UI', Arial, sans-serif; margin: 20px; background-color: #f5f5f5; color: #333; }
          .form-container { max-width: 1400px; margin: 0 auto; background: white; border-radius: 10px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); overflow: hidden; }
          .navbar { background: linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%); padding: 15px 20px; display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #e3f2fd; }
          .logo { width: 70px; height: 70px; border-radius: 50%; border: 2px solid white; margin-right: 20px; }
          .school-name { font-size: 24px; font-weight: 600; color: white; text-shadow: 1px 1px 2px rgba(0,0,0,0.2); }
          .report-title { text-align: center; padding: 15px; color: #2196F3; font-size: 20px; font-weight: 500; background-color: #e3f2fd; margin: 0; }
          .table-container { margin: 20px; overflow-x: auto; max-width: 100%; }
          table { width: 100%; min-width: 1200px; border-collapse: separate; border-spacing: 0; }
          th { background: linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%); color: white; padding: 12px 15px; font-weight: 600; text-transform: uppercase; font-size: 13px; letter-spacing: 0.5px; border-bottom: 2px solid #e3f2fd; white-space: nowrap; position: sticky; top: 0; z-index: 1; }
          td { padding: 12px 15px; border-bottom: 1px solid #eee; font-size: 13px; color: #444; white-space: nowrap; }
          tr:nth-child(even) { background-color: #fafafa; }
          tr:hover { background-color: #e3f2fd; transition: background-color 0.2s ease; }
          .print-button { background: linear-gradient(45deg, #4CAF50, #66BB6A); border: none; color: white; padding: 12px 24px; border-radius: 25px; font-size: 14px; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.2); transition: all 0.3s ease; display: flex; align-items: center; margin-bottom: 10px; }
          .print-button:hover { background: linear-gradient(45deg, #45a049, #5daf53); transform: translateY(-2px); box-shadow: 0 4px 8px rgba(0,0,0,0.3); }
          .print-icon { margin-right: 8px; font-size: 18px; }
          .amount { text-align: right; }
        </style>
      </head>
      <body>
        <div class="form-container">
          ${schoolData
            .map(
              (item) => `
                <div class="navbar">
                  <img class="logo" src="${logoDataUrl}" alt="Logo" />
                  <div class="school-name">${item.schoolName}</div>
                  <button class="print-button" onclick="window.print()">
                    <span class="print-icon">🖨️</span> Print Report
                  </button>
                </div>
              `
            )
            .join("")}
          <div class="report-title">Fee Payment Report (${formattedFromDate} - ${formattedToDate})</div>
          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Admission No</th>
                  <th>Roll No</th>
                  <th>Class</th>
                  <th>Section</th>
                  <th>Month</th>
                  <th>Session</th>
                  <th>Payment Date</th>
                  <th>Total Fee</th>
                  <th>Paid Amount</th>
                  <th>Balance</th>
                  <th>Fine</th>
                  <th>Receipt No</th>
                  <th>Payment Mode</th>
                </tr>
              </thead>
              <tbody>
                ${response.data
                  .map(
                    (item) => `
                      <tr>
                        <td>${item.studentName || "-"}</td>
                        <td>${item.admissionNo || "-"}</td>
                        <td>${item.rollNo || "-"}</td>
                        <td>${item.className || "-"}</td>
                        <td>${item.sectionName || "-"}</td>
                        <td>${item.monthName || "-"}</td>
                        <td>${item.session || "-"}</td>
                        <td>${item.feePaymentDate || "-"}</td>
                        <td>₹${(item.totalFee || 0).toFixed(2)}</td>
                        <td>₹${(item.paidAmount || 0).toFixed(2)}</td>
                        <td>₹${(item.balanceAmount || 0).toFixed(2)}</td>
                        <td>₹${(item.fineAmount || 0).toFixed(2)}</td>
                        <td>${item.recieptNo || "-"}</td>
                        <td>${item.paymentMode || "-"}</td>
                      </tr>
                    `
                  )
                  .join("")}
              </tbody>
            </table>
          </div>
        </div>
      </body>
    </html>
  `);
    } catch (error) {
      console.error("Error generating report:", error);
    }
  };

  // Generate PDF
  const handlePrint = () => {
    if (!paymentData?.length) return;
    const doc = new jsPDF();
    doc.setFont("times", "bold");
    doc.setFontSize(16);
    doc.text("Fee Payment Report", 105, 20, null, null, "center");
    doc.autoTable({
      head: [
        ["Student Name", "Admission No", "Roll No", "Payment Date", "Total Fee", "Paid Amount", "Balance", "Fine", "Receipt No", "Month", "Class", "Section", "Payment Mode", "Session"],
      ],
      body: paymentData.map((item) => [
        item.studentName || "-",
        item.admissionNo || "-",
        item.rollNo || "-",
        item.feePaymentDate || "-",
        `₹${(item.totalFee || 0).toFixed(2)}`,
        `₹${(item.paidAmount || 0).toFixed(2)}`,
        `₹${(item.balanceAmount || 0).toFixed(2)}`,
        `₹${(item.fineAmount || 0).toFixed(2)}`,
        item.recieptNo || "-",
        item.monthName || "-",
        item.className || "-",
        item.sectionName || "-",
        item.paymentMode || "-",
        item.session || "-",
      ]),
      styles: { fontSize: 9, halign: "center", font: "times" },
      headStyles: {
        fillColor: [75, 108, 183],
        textColor: [255, 255, 255],
        lineWidth: 0.1,
        font: "times",
        fontStyle: "bold",
      },
      theme: "grid",
      margin: { left: 10, right: 10 },
    });
    const pdfUrl = doc.output("bloburl");
    window.open(pdfUrl, "_blank");
  };

  // Generate CSV
  const generateCSV = () => {
    if (!paymentData?.length) return;
    const headers = [
      "Student Name",
      "Admission No",
      "Roll No",
      "Payment Date",
      "Total Fee",
      "Paid Amount",
      "Balance Amount",
      "Fine Amount",
      "Receipt No",
      "Month",
      "Class",
      "Section",
      "Payment Mode",
      "Session",
    ];
    const csvContent = [
      headers.join(","),
      ...paymentData.map((item) =>
        [
          item.studentName || "-",
          item.admissionNo || "-",
          item.rollNo || "-",
          item.feePaymentDate || "-",
          item.totalFee || 0,
          item.paidAmount || 0,
          item.balanceAmount || 0,
          item.fineAmount || 0,
          item.recieptNo || "-",
          item.monthName || "-",
          item.className || "-",
          item.sectionName || "-",
          item.paymentMode || "-",
          item.session || "-",
        ].join(",")
      ),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fee_payment_report_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  // useEffect
  useEffect(() => {
    fetchSchoolDetails();
    fetchPaymentDetails();
  }, []);

  // Render
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minHeight: "100vh" }}>
      <LoadingBar progress={loadingBarProgress} color="rgba(75,108,183,1)" height={5} />
      <Card sx={{ maxWidth: "100%", mx: "auto", boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <CurrencyExchangeIcon sx={{ fontSize: 40, color: "rgba(75,108,183,1)", mr: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: "bold", color: "rgba(49, 70, 119, 1)" }}>
              Fee Deposit Report
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />

          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: "200px" }}
            />
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: "200px" }}
            />
          </Box>

          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={generateReport}
              sx={{
                background: "linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)",
                borderRadius: "25px",
                padding: "10px 25px",
                width: "150px",
                boxShadow: "0 3px 5px 2px rgba(75,108,183,0.3)",
                "&:hover": {
                  background: "linear-gradient(110deg, rgba(24,40,72,1) 11.3%, rgba(75,108,183,1) 99.3%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 5px 10px rgba(75,108,183,0.4)",
                },
              }}
            >
              Generate Report
            </Button>
            {/* <Button
              variant="contained"
              onClick={handlePrint}
              sx={{
                background: "linear-gradient(45deg, #f44336 30%, #ef5350 90%)",
                borderRadius: "25px",
                padding: "10px 25px",
                width: "150px",
                boxShadow: "0 3px 5px 2px rgba(244, 67, 54, 0.3)",
                "&:hover": {
                  background: "linear-gradient(45deg, #d32f2f 30%, #e53935 90%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 5px 10px rgba(244, 67, 54, 0.4)",
                },
              }}
            >
              Print PDF
            </Button> */}
            <Button
              variant="contained"
              onClick={generateCSV}
              sx={{
                background: "linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)",
                borderRadius: "25px",
                padding: "10px 25px",
                width: "150px",
                boxShadow: "0 3px 5px 2px rgba(76, 175, 80, 0.3)",
                "&:hover": {
                  background: "linear-gradient(45deg, #45a049 30%, #5daf53 90%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 5px 10px rgba(76, 175, 80, 0.4)",
                },
              }}
            >
              Download CSV
            </Button>
          </Box>

          {paymentData.length > 0 ? (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
                Payment Records
              </Typography>
              <Table sx={{ border: "1px solid #ccc", minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ background: "linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)" }}>
                    <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Date</TableCell>
                    <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Amount</TableCell>
                    <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentData
                    .filter((record) => record.feePaymentDate)
                    .map((record, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "#f9f9f9" },
                          "&:hover": { backgroundColor: "#f0f0f0" },
                        }}
                      >
                        <TableCell align="center">{formatDateForInput(record.feePaymentDate)}</TableCell>
                        <TableCell align="center">₹{record.amount}</TableCell>
                        <TableCell align="center">
                          <span
                            style={{
                              padding: "4px 8px",
                              borderRadius: "12px",
                              color: "white",
                              backgroundColor:
                                record.paymentStatus === "Paid"
                                  ? "#4CAF50"
                                  : record.paymentStatus === "Pending"
                                  ? "#FF9800"
                                  : "#F44336",
                            }}
                          >
                            {record.paymentStatus}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>
              No payment data available.
            </Typography>
          )}
        </CardContent>
      </Card>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="warning" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentPage;
